// store/aiImportRuntime.ts
import { AiImportResponseTypes } from "@/constants/enums";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export type AiImportLiveStep = {
  id: number;
  node: string;
  type: AiImportResponseTypes.PROGRESS;
  title: string;
  content: string;
};

export type AiImportLiveState = {
  lastEventId?: string | null;
  steps: AiImportLiveStep[];
  isResuming?: boolean;
};

type AiImportRuntimeState = {
  activeImports: Record<number, AiImportLiveState>;
  isUploading: boolean;
};

type AiImportRuntimeActions = {
  beginImport: (_aiImportId: number, _isResuming?: boolean) => void;
  endImport: (_aiImportId?: number) => void;
  setUploading: (_isUploading: boolean) => void;
  setLastEventId: (_aiImportId: number, _lastEventId: string | null | undefined) => void;
  setSteps: (_aiImportId: number, _steps: AiImportLiveStep[], _lastEventId?: string | null) => void;
  appendStep: (_aiImportId: number, _step: AiImportLiveStep) => void;
  clearSteps: (_aiImportId: number) => void;
  reset: () => void;
};

export const useAiImportRuntime = create<AiImportRuntimeState & AiImportRuntimeActions>()(
  persist(
    (set) => ({
      activeImports: {},
      isUploading: false,

      beginImport: (aiImportId: number, isResuming = false) =>
        set((state) => ({
          activeImports: {
            ...state.activeImports,
            [aiImportId]: {
              ...(state.activeImports[aiImportId] ?? { lastEventId: null, steps: [] }),
              isResuming,
            },
          },
        })),

      endImport: (aiImportId?: number) =>
        set((state) => {
          if (typeof aiImportId === "number") {
            const next = { ...state.activeImports };
            delete next[aiImportId];
            return {
              activeImports: next,
            };
          }
          return { activeImports: {} };
        }),

      setUploading: (isUploading: boolean) => set({ isUploading }),

      setLastEventId: (aiImportId: number, lastEventId: string | null | undefined) =>
        set((state) => ({
          activeImports: {
            ...state.activeImports,
            [aiImportId]: {
              ...(state.activeImports[aiImportId] ?? { steps: [] }),
              lastEventId: lastEventId ?? null,
            },
          },
        })),

      setSteps: (aiImportId: number, steps: AiImportLiveStep[], lastEventId?: string | null) =>
        set((state) => ({
          activeImports: {
            ...state.activeImports,
            [aiImportId]: {
              ...(state.activeImports[aiImportId] ?? { lastEventId: null, steps: [] }),
              lastEventId: typeof lastEventId === "undefined" ? (state.activeImports[aiImportId]?.lastEventId ?? null) : (lastEventId ?? null),
              steps: steps,
            },
          },
        })),

      appendStep: (aiImportId: number, step: AiImportLiveStep) =>
        set((state) => ({
          activeImports: {
            ...state.activeImports,
            [aiImportId]: {
              ...(state.activeImports[aiImportId] ?? { lastEventId: null, steps: [] }),
              steps: [...(state.activeImports[aiImportId]?.steps ?? []), step],
              isResuming: false, // Clear resuming flag once we receive a step
            },
          },
        })),

      clearSteps: (aiImportId: number) =>
        set((state) => ({
          activeImports: {
            ...state.activeImports,
            [aiImportId]: {
              ...(state.activeImports[aiImportId] ?? { lastEventId: null, steps: [] }),
              steps: [],
            },
          },
        })),

      reset: () => set({ activeImports: {}, isUploading: false }),
    }),
    {
      name: "ai-import-runtime",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({ activeImports: state.activeImports }),
    }
  )
);
